import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const baseCellStyle = {
    border: '1px dashed rgba(233, 30, 99, 0.35)',
    borderRadius: '12px',
    padding: '16px',
    backgroundColor: '#fff',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    textAlign: 'left',
    minHeight: '140px',
};

const compactCellStyle = {
    padding: '10px',
    borderRadius: '10px',
    minHeight: '54px',
};

const activeCellStyle = {
    borderColor: '#e91e63',
    boxShadow: '0 6px 18px rgba(233, 30, 99, 0.15)',
    backgroundColor: '#fff8fb',
};

const disabledCellStyle = {
    opacity: 0.5,
    cursor: 'not-allowed',
    backgroundColor: '#f8f9fa',
};

const buttonBaseStyle = {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'inherit',
    color: 'inherit',
};

const numberBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
    borderRadius: '999px',
    backgroundColor: 'rgba(233, 30, 99, 0.12)',
    color: '#e91e63',
    fontWeight: 600,
    marginBottom: '8px',
    fontSize: '0.95rem',
};

const compactNumberBadgeStyle = {
    width: '26px',
    height: '26px',
    minWidth: '26px',
    fontSize: '0.85rem',
    marginBottom: '4px',
};

const titleStyle = {
    fontWeight: 600,
    fontSize: '0.95rem',
    marginBottom: '8px',
    color: '#3c4858',
};

const compactTitleStyle = {
    fontSize: '0.85rem',
    marginBottom: '4px',
};

const metaStyle = {
    fontSize: '0.8rem',
    color: '#8898aa',
    lineHeight: 1.35,
};

const actionsRowStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto',
};

const compactActionsRowStyle = {
    justifyContent: 'center',
    marginTop: '8px',
};

const fullscreenActionStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 600,
};

const compactFullscreenActionStyle = {
    gap: '4px',
    fontSize: '0.85rem',
};

const emptyLabelStyle = {
    fontWeight: 500,
    fontSize: '0.85rem',
    color: '#b0bec5',
};

const DisplayWallGridControl = ({
    columns,
    rows,
    streams,
    onCellFullscreen,
    activeOrder,
    busy,
}) => {
    const gridCells = Math.max(columns * rows, 0);

    const streamMap = useMemo(() => {
        const map = new Map();
        streams.forEach((stream) => {
            if (typeof stream.order === 'number') {
                map.set(stream.order, stream);
            }
        });
        return map;
    }, [streams]);

    const isCompact = gridCells > 12;

    const cells = useMemo(() => {
        return Array.from({ length: gridCells }, (_, index) => {
            const stream = streamMap.get(index) || null;
            return { index, stream };
        });
    }, [gridCells, streamMap]);

    if (!columns || !rows || gridCells === 0) {
        return null;
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <h5 className="mb-0">Wall grid control</h5>
                <div className="text-muted small">Click a cell to trigger fullscreen on the physical wall.</div>
            </div>
            <div className="card-body">
                <div
                    className="display-wall-grid-control"
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                        gap: isCompact ? '8px' : '14px',
                    }}
                >
                    {cells.map(({ index, stream }) => {
                        const hasStream = !!stream;
                        const isActive = typeof activeOrder === 'number' && activeOrder === index;
                        const cellStyle = {
                            ...baseCellStyle,
                            ...(isCompact ? compactCellStyle : {}),
                            ...(isActive ? activeCellStyle : {}),
                            ...(!hasStream ? disabledCellStyle : {}),
                        };
                        const buttonStyle = {
                            ...buttonBaseStyle,
                            cursor: !hasStream || busy ? 'not-allowed' : 'pointer',
                        };
                        const fullscreenAction = {
                            ...fullscreenActionStyle,
                            ...(isCompact ? compactFullscreenActionStyle : {}),
                            color: hasStream ? '#e91e63' : '#b0bec5',
                        };

                        return (
                            <div key={index} style={cellStyle}>
                                <button
                                    type="button"
                                    style={buttonStyle}
                                    onClick={() => hasStream && !busy && onCellFullscreen(index)}
                                    disabled={!hasStream || busy}
                                    aria-label={hasStream ? `Fullscreen stream ${index}` : `Empty position ${index}`}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <div style={{
                                            ...numberBadgeStyle,
                                            ...(isCompact ? compactNumberBadgeStyle : {}),
                                        }}
                                        >
                                            {index+1}
                                        </div>
                                        {hasStream ? (
                                            <>
                                                <div style={{
                                                    ...titleStyle,
                                                    ...(isCompact ? compactTitleStyle : {}),
                                                }}>
                                                    {stream.title || 'Untitled stream'}
                                                </div>
                                                {!isCompact && (
                                                    <div style={metaStyle}>
                                                        {stream.typeLabel}
                                                        {stream.flux ? ` · Flux #${stream.flux.id}` : ''}
                                                    </div>
                                                )}
                                            </>
                                        ) : (!isCompact ? (
                                            <div style={emptyLabelStyle}>Empty slot</div>
                                        ) : null)}
                                    </div>
                                    <div style={{
                                        ...actionsRowStyle,
                                        ...(isCompact ? compactActionsRowStyle : {}),
                                    }}>
                                        <div style={fullscreenAction}>
                                            <span
                                                className="material-icons"
                                                aria-hidden="true"
                                                style={{ fontSize: isCompact ? '1rem' : '1.1rem' }}
                                            >
                                                fullscreen
                                            </span>
                                            {!isCompact && <span>Fullscreen</span>}
                                        </div>
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

DisplayWallGridControl.propTypes = {
    columns: PropTypes.number.isRequired,
    rows: PropTypes.number.isRequired,
    streams: PropTypes.arrayOf(PropTypes.shape({
        order: PropTypes.number.isRequired,
        title: PropTypes.string,
        typeLabel: PropTypes.string,
        flux: PropTypes.shape({
            id: PropTypes.number,
        }),
    })).isRequired,
    onCellFullscreen: PropTypes.func.isRequired,
    activeOrder: PropTypes.number,
    busy: PropTypes.bool,
};

DisplayWallGridControl.defaultProps = {
    activeOrder: null,
    busy: false,
};

export default DisplayWallGridControl;
